import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RestaurantOption } from '../../data/googleAccounts';

/** How many matches the list shows at once; typing narrows the rest. */
export const COMBO_MAX = 50;

function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase()
    .trim();
}

/** The listed rows: suggestions first, then what matches the typed text. */
export function comboRows(
  restaurants: RestaurantOption[],
  suggestions: RestaurantOption[],
  query: string,
): { suggested: RestaurantOption[]; matches: RestaurantOption[]; more: number } {
  const q = fold(query);
  const hit = (r: RestaurantOption) => !q || fold(`${r.name} ${r.city || ''}`).includes(q);
  const openable = new Map(restaurants.map(r => [r.id, r]));
  // A suggestion is listed only when the person can open it.
  const suggested = suggestions
    .filter(s => openable.has(s.id))
    .map(s => openable.get(s.id)!)
    .filter(hit);
  const seen = new Set(suggested.map(s => s.id));
  const all = restaurants.filter(r => !seen.has(r.id) && hit(r));
  return { suggested, matches: all.slice(0, COMBO_MAX), more: Math.max(0, all.length - COMBO_MAX) };
}

/**
 * One searchable combobox for the restaurant a property feeds (ARIA 1.2
 * combobox with a listbox popup): typing filters by name or city, Up and
 * Down move through the options, Enter picks, Escape closes the list (and
 * clears the typed text when the list is already closed). Suggestions come
 * first, then matches, each with its city. Only `restaurants` (the ones the
 * person can open, or the first pick's customer) are offered.
 */
export function RestaurantCombobox({
  id,
  restaurants,
  suggestions = [],
  value,
  onChange,
  describedBy,
}: {
  id: string;
  restaurants: RestaurantOption[];
  suggestions?: RestaurantOption[];
  value: string;
  onChange: (restaurantId: string) => void;
  /** Ids of hints under the field (for example the one-customer reason). */
  describedBy?: string;
}) {
  const { t } = useTranslation('growth');
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const chosen = restaurants.find(r => r.id === value) || suggestions.find(s => s.id === value) || null;
  const { suggested, matches, more } = useMemo(
    () => comboRows(restaurants, suggestions, editing ? query : ''),
    [restaurants, suggestions, query, editing],
  );
  const rows = [...suggested, ...matches];
  const activeRow = open ? rows[Math.min(active, rows.length - 1)] : undefined;
  const optionId = (rid: string) => `${listId}-${rid.replace(/[^\w-]/g, '_')}`;

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  const pick = (option: RestaurantOption) => {
    onChange(option.id);
    setQuery('');
    setEditing(false);
    setOpen(false);
  };

  // Escape must close the list before the dialog around it sees the key
  // (the dialog listens on its own node, ahead of React's root handler).
  const state = useRef({ open, editing, query });
  state.current = { open, editing, query };
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const now = state.current;
      if (now.open || (now.editing && now.query)) {
        event.stopPropagation();
        event.preventDefault();
        if (now.open) setOpen(false);
        else {
          setQuery('');
          setEditing(false);
        }
      }
    };
    input.addEventListener('keydown', onKey);
    return () => input.removeEventListener('keydown', onKey);
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive(at => (rows.length ? (Math.min(at, rows.length - 1) + step + rows.length) % rows.length : 0));
    } else if (event.key === 'Home' && open) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End' && open) {
      event.preventDefault();
      setActive(Math.max(0, rows.length - 1));
    } else if (event.key === 'Enter') {
      if (open && activeRow) {
        event.preventDefault();
        pick(activeRow);
      }
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!activeRow) return;
    document.getElementById(optionId(activeRow.id))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeRow?.id]);

  const option = (row: RestaurantOption) => {
    const isActive = activeRow?.id === row.id;
    return (
      <div
        key={row.id}
        id={optionId(row.id)}
        role="option"
        aria-selected={row.id === value}
        className={`acx-combo-option${isActive ? ' is-active' : ''}${row.id === value ? ' is-chosen' : ''}`}
        onMouseDown={event => {
          // Keep focus in the field; the click picks.
          event.preventDefault();
          pick(row);
        }}
        onMouseMove={() => setActive(rows.indexOf(row))}
      >
        <span className="acx-combo-name">{row.name}</span>
        {row.city ? <span className="acx-combo-city">{row.city}</span> : null}
      </div>
    );
  };

  const shown = editing ? query : chosen ? (chosen.city ? `${chosen.name} · ${chosen.city}` : chosen.name) : '';

  return (
    <div className="acx-combo">
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="acx-input acx-combo-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeRow ? optionId(activeRow.id) : undefined}
        aria-describedby={describedBy}
        autoComplete="off"
        spellCheck={false}
        placeholder={t('accountConnect.comboPlaceholder')}
        value={shown}
        onChange={event => {
          setEditing(true);
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={event => event.currentTarget.select()}
        onClick={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          setEditing(false);
          setQuery('');
        }}
        onKeyDown={onKeyDown}
      />
      <div
        id={listId}
        role="listbox"
        aria-label={t('accountConnect.pickRestaurant')}
        className="acx-combo-list"
        hidden={!open}
      >
        {suggested.length ? (
          <div role="group" aria-labelledby={`${listId}-suggested`}>
            <div role="presentation" id={`${listId}-suggested`} className="acx-combo-head">
              {t('accountConnect.comboSuggested')}
            </div>
            {suggested.map(option)}
          </div>
        ) : null}
        {matches.length ? (
          <div role="group" aria-labelledby={`${listId}-matches`}>
            <div role="presentation" id={`${listId}-matches`} className="acx-combo-head">
              {editing && query.trim()
                ? t('accountConnect.comboMatches', { query: query.trim() })
                : t('accountConnect.comboAll')}
            </div>
            {matches.map(option)}
          </div>
        ) : null}
        {!rows.length ? (
          <div role="presentation" className="acx-combo-empty">
            {t('accountConnect.comboNone', { query: query.trim() })}
          </div>
        ) : null}
        {more ? (
          <div role="presentation" className="acx-combo-empty">
            {t('accountConnect.comboMore', { count: more })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
